import { useCallback, useEffect, useRef, useState } from "react";
import {
  listEasternProducts,
  listSaleCatalogProducts,
  type ListEasternProductsParams,
  type ListSaleCatalogProductsParams,
} from "@workspace/api-client-react";
import type { StoreProduct } from "@/lib/store";

type CatalogOptions = {
  enabled?: boolean;
  category?: string;
  collectionHandles?: string[];
  search?: string;
  sort?: string;
  saleOnly?: boolean;
  brandSlug?: string;
};

export function useCatalog({
  enabled = true,
  category,
  collectionHandles,
  search,
  sort = "featured",
  saleOnly = false,
  brandSlug,
}: CatalogOptions = {}) {
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [loadMoreError, setLoadMoreError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [pollKey, setPollKey] = useState(0);
  const syncWaitStartedAt = useRef<number | null>(null);
  const requestGeneration = useRef(0);
  const requestIdentity = `${enabled}|${category ?? ""}|${collectionHandles?.join(",") ?? ""}|${search ?? ""}|${sort}|${saleOnly}|${brandSlug ?? ""}|${reloadKey}`;
  const lastRequestIdentity = useRef(requestIdentity);
  const params = {
    ...(category ? { category: category as ListEasternProductsParams["category"] } : {}),
    ...(collectionHandles?.length ? { collectionHandles: collectionHandles.join(",") } : {}),
    ...(search?.trim() ? { search: search.trim() } : {}),
    sort: (sort || "featured") as ListEasternProductsParams["sort"],
    ...(saleOnly ? { saleOnly: true } : {}),
  };
  const saleParams: ListSaleCatalogProductsParams = {
    ...(brandSlug ? { brandSlug } : {}),
    sort: (sort || "featured") as ListSaleCatalogProductsParams["sort"],
  };

  useEffect(() => {
    if (lastRequestIdentity.current !== requestIdentity) {
      lastRequestIdentity.current = requestIdentity;
      syncWaitStartedAt.current = null;
    }
    requestGeneration.current += 1;
    if (!enabled) {
      syncWaitStartedAt.current = null;
      setProducts([]);
      setTotal(0);
      setPage(1);
      setLoading(false);
      setLoadingMore(false);
      setError("");
      setLoadMoreError("");
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    setError("");
    setLoadMoreError("");
    setLoadingMore(false);
    setLoading(true);

    const handleError = (error: unknown) => {
      if (cancelled) return;
      console.error("Storefront catalog request failed", error);
      const message = saleOnly
        ? "We could not load sale pieces right now. Please try again."
        : "We could not load the Eastern collection right now. Please try again.";
      setError(controller.signal.aborted
        ? saleOnly ? "Loading sale pieces took too long. Please try again." : "The Eastern collection took too long to load. Please try again."
        : message);
      setLoading(false);
    };
    if (saleOnly) {
      void listSaleCatalogProducts({ ...saleParams, page: 1 }, { signal: controller.signal })
        .then(result => {
          if (cancelled) return;
          setProducts(result.products as StoreProduct[]);
          setTotal(result.total);
          setPage(result.page);
          setLoading(false);
          syncWaitStartedAt.current = null;
        })
        .catch(handleError)
        .finally(() => window.clearTimeout(timeout));
    } else {
      void listEasternProducts({ ...params, page: 1 }, { signal: controller.signal })
        .then(result => {
          if (cancelled) return;
          setProducts(result.products);
          setTotal(result.total);
          setPage(result.page);
          if (!result.total && !result.syncing && result.syncError) {
            setError("We could not load the Eastern collection right now. Please try again.");
          }
          const waitingForFirstSync = result.syncing && result.total === 0;
          setLoading(waitingForFirstSync);
          if (waitingForFirstSync) {
            syncWaitStartedAt.current ??= Date.now();
            if (Date.now() - syncWaitStartedAt.current >= 30_000) {
              setError("The Eastern collection is taking longer than expected to load. Please try again.");
              setLoading(false);
              return;
            }
            window.setTimeout(() => {
              if (!cancelled) setPollKey(value => value + 1);
            }, 2500);
          } else {
            syncWaitStartedAt.current = null;
          }
        })
        .catch(handleError)
        .finally(() => window.clearTimeout(timeout));
    }

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [enabled, category, collectionHandles, search, sort, saleOnly, brandSlug, reloadKey, pollKey, requestIdentity]);

  const loadMore = useCallback(async () => {
    if (!enabled || loading || loadingMore || products.length >= total) return;
    const generation = requestGeneration.current;
    setLoadingMore(true);
    setLoadMoreError("");
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    try {
      const result = saleOnly
        ? await listSaleCatalogProducts({ ...saleParams, page: page + 1 }, { signal: controller.signal })
        : await listEasternProducts({ ...params, page: page + 1 }, { signal: controller.signal });
      if (generation !== requestGeneration.current) return;
      setProducts(current => {
        const known = new Set(current.map(product => product.id));
        return [...current, ...(result.products as StoreProduct[]).filter(product => !known.has(product.id))];
      });
      setPage(result.page);
      setTotal(result.total);
    } catch (error) {
      if (generation !== requestGeneration.current) return;
      console.error("Storefront catalog pagination failed", error);
      setLoadMoreError(controller.signal.aborted
        ? "Loading more pieces took too long. Please try again."
        : "We could not load more pieces right now. Please try again.");
    } finally {
      window.clearTimeout(timeout);
      if (generation === requestGeneration.current) setLoadingMore(false);
    }
  }, [enabled, category, collectionHandles, search, sort, saleOnly, brandSlug, page, products, total, loading, loadingMore]);

  return {
    products,
    total,
    loading,
    loadingMore,
    error,
    loadMoreError,
    hasMore: products.length < total,
    loadMore,
    refresh: () => setReloadKey(value => value + 1),
  };
}